import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    IconButton
} from "@mui/material";

/* ✅ Icons */
import { AiOutlineRead, AiFillStar } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopIcon from "@mui/icons-material/Stop";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1516116216624-53e697fedbea";

/* ✅ Styled Components - Premium Dark Glass Theme */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/**
 * TypeScriptSummary Component
 * * Final review of the TypeScript course.
 * Includes Key Highlights and Practical Challenges for students.
 */
export default function TypeScriptSummary() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* ✅ UI & Voice State */
    const [showScroll, setShowScroll] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);

    /* ✅ FAB Visibility Logic */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    /* ✅ Audio Controls */
    const handleSpeak = (text) => {
        window.speechSynthesis.cancel();
        if (isSpeaking) {
            setIsSpeaking(false);
            return;
        }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.95;
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
    };

    const stopSpeech = () => {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
    };

    /* ✅ Summary Data */
    const summaryPoints = useMemo(() => [
        "Basic Types: number, string, boolean, arrays, tuples",
        "Special Types: any, unknown, void, never",
        "Type vs Interface: declaration, extension, merging",
        "Generics: for reusable, adaptable logic",
        "Enums: named constants for better readability",
        "Type Guards: runtime type safety with confidence",
        "Utility Types: Partial, Omit, Pick, Readonly, and more"
    ], []);

    const challenges = useMemo(() => [
        "Create a generic FormField<T> component with props for label and value.",
        "Build a union type for app routes to restrict navigation logic.",
        "Use Partial and Pick to construct admin-safe user profile views.",
        "Write a type guard to distinguish between a File object and a URL string."
    ], []);

    return (
        <PageWrapper>
            {/* 🏆 Header Section */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h4" : "h3"}
                    sx={{
                        fontWeight: 800,
                        backgroundColor: "goldenrod",
                        color: "#100720",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        display: "inline-block",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    TS FUNDAMENTALS
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 🎯 Completion Banner */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h5" fontWeight={800} color="goldenrod">
                                Summary & Practice
                            </Typography>
                            <IconButton
                                onClick={() => handleSpeak("Congratulations! You’ve covered a rich spectrum of TypeScript—from basic primitives to advanced interface magic. Here is a recap to lock in your knowledge.")}
                                sx={{ color: "goldenrod" }}
                            >
                                <VolumeUpIcon />
                            </IconButton>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.7 }}>
                            You’ve successfully navigated the core pillars of TypeScript! From strictly typing your variables to building complex, reusable logic with Generics, you now have the tools to build professional, bug-resistant React applications.
                        </Typography>
                    </SectionCard>
                </Grid>

                {/* ✅ Recap Highlights */}
                <Grid item xs={12} md={6}>
                    <SectionCard>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                <AssignmentTurnedInIcon />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Key Highlights</Typography>
                        </Box>
                        <Stack spacing={1.5}>
                            {summaryPoints.map((point, index) => (
                                <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <AiFillStar color="goldenrod" />
                                    <Typography variant="body2" sx={{ opacity: 0.85 }}>{point}</Typography>
                                </Box>
                            ))}
                        </Stack>
                    </SectionCard>
                </Grid>

                {/* ✅ Challenge Section */}
                <Grid item xs={12} md={6}>
                    <SectionCard>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <Avatar sx={{ bgcolor: "rgba(46, 125, 50, 0.2)", color: "seagreen" }}>
                                <FitnessCenterIcon />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Challenge Yourself</Typography>
                        </Box>
                        <Stack spacing={2}>
                            {challenges.map((challenge, index) => (
                                <Box key={index} sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: 2, borderLeft: "4px solid goldenrod" }}>
                                    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>{challenge}</Typography>
                                </Box>
                            ))}
                        </Stack>
                    </SectionCard>
                </Grid>

                {/* ✅ Final Action */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4, mb: 10 }}>
                    <Link to="/typescript-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            RESTART COURSE
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Control FABs */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                {isSpeaking && (
                    <Zoom in={isSpeaking}>
                        <Fab size="medium" color="error" onClick={stopSpeech}>
                            <StopIcon />
                        </Fab>
                    </Zoom>
                )}
                <Zoom in={showScroll}>
                    <Fab
                        size="small"
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        sx={{ bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                    >
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </PageWrapper>
    );
}
